<template>
  <div>
    <app-template />
    <div class="uk-container uk-container-large">
      <h1>Physical testing</h1>
      <p class="testing-intro">
        Pick a squad and a player, then record today's results — the
        numbers save as you go, so work straight down the line.
      </p>

      <div class="testing-pickers">
        <div class="form__item">
          <label class="form__label" for="testingTeam">Squad</label>
          <select id="testingTeam" class="form__input" v-model="selectedTeamId" @change="loadPlayers">
            <option value="" disabled>Select a squad</option>
            <option v-for="team in teams" :key="team.id" :value="team.id">{{ team.teamName }}</option>
          </select>
        </div>
        <div class="form__item">
          <label class="form__label" for="testingPlayer">Player</label>
          <select id="testingPlayer" class="form__input" v-model="selectedPlayerId" :disabled="!players.length">
            <option value="" disabled>{{ players.length ? "Select a player" : "Pick a squad first" }}</option>
            <option v-for="p in players" :key="p.id" :value="p.id">{{ p.name }}</option>
          </select>
        </div>
        <div class="form__item player-nav" v-if="selectedPlayer">
          <button class="btn btn-outline-secondary btn-sm" :disabled="!prevPlayer" @click="selectedPlayerId = prevPlayer.id">
            <i class="bi bi-chevron-left"></i> Previous
          </button>
          <button class="btn btn-outline-secondary btn-sm" :disabled="!nextPlayer" @click="selectedPlayerId = nextPlayer.id">
            Next <i class="bi bi-chevron-right"></i>
          </button>
        </div>
      </div>

      <div v-if="loadingPlayers" class="loading-state"><p>Loading players…</p></div>

      <div v-else-if="selectedPlayer" class="testing-player">
        <div class="player-strip">
          <img :src="avatar(selectedPlayer.userImage)" alt="" />
          <div>
            <h2>{{ selectedPlayer.name }}</h2>
            <p>
              {{ selectedTeamName }}
              <span v-if="selectedPlayer.position"> · {{ selectedPlayer.position }}</span>
              <span v-if="selectedPlayer.dob"> · DOB {{ formatDob(selectedPlayer.dob) }}</span>
            </p>
          </div>
          <button class="btn btn-outline-primary bioband-btn" @click="showBioBanding = true">
            <i class="bi bi-rulers"></i> Growth &amp; maturation
          </button>
        </div>

        <PlayerPhysicalPerformanceStats :key="selectedPlayer.id" :selectedUserValue="selectedPlayer.id"
          :selectedUserData="selectedPlayer" :userData="userData" :currentSeason="currentSeason"
          :getLastSeason="lastSeason" :staffOverride="true" />

        <div v-if="showBioBanding" class="modal-overlay" @click.self="showBioBanding = false">
          <div class="modal-dialog modal-xl">
            <div class="modal-content bioband-modal-content">
              <div class="modal-body p-0">
                <BioBandingCalculator :playerName="selectedPlayer.name" :playerId="selectedPlayer.id"
                  :playerDob="selectedPlayer.dob" :playerGender="selectedPlayer.gender" :currentUserId="userData.id"
                  @close="showBioBanding = false" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-else class="empty-state">
        <i class="bi bi-clipboard-data"></i>
        <p>Select a squad and a player to start recording results.</p>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import dayjs from "dayjs";
import AppTemplate from "../layouts/App";
import PlayerPhysicalPerformanceStats from "@/components/PlayerPhysicalPerformanceStats";
import BioBandingCalculator from "@/components/BioBandingCalculator";
import { Teams } from "@/services/teams-service.js";
import { PlayerPhysicalPerformance } from "@/services/player-physical-performance-service.js";
import { avatarUrl } from "@/utils/avatar";

/**
 * One-screen testing-day workflow: squad → player → enter results. Built
 * for the restricted SportsScience role (volunteers who may record
 * testing data for any squad but must not see the profile page), and
 * handy for coaches on a testing night. The roster comes from a guarded,
 * minimal endpoint — never the full users row.
 */
export default {
  name: "Testing",
  components: { AppTemplate, PlayerPhysicalPerformanceStats, BioBandingCalculator },
  data() {
    return {
      teams: [],
      players: [],
      selectedTeamId: "",
      selectedPlayerId: "",
      loadingPlayers: false,
      showBioBanding: false,
    };
  },
  computed: {
    ...mapGetters(["getProfile"]),
    userData() {
      return this.getProfile || {};
    },
    selectedPlayer() {
      return this.players.find((p) => p.id === this.selectedPlayerId) || null;
    },
    selectedTeamName() {
      const t = this.teams.find((t) => t.id === this.selectedTeamId);
      return t ? t.teamName : "";
    },
    prevPlayer() {
      const i = this.players.findIndex((p) => p.id === this.selectedPlayerId);
      return i > 0 ? this.players[i - 1] : null;
    },
    nextPlayer() {
      const i = this.players.findIndex((p) => p.id === this.selectedPlayerId);
      return i >= 0 && i < this.players.length - 1 ? this.players[i + 1] : null;
    },
    // Same Aug–May season window the profile page uses.
    currentSeason() {
      const now = new Date();
      const y = now.getFullYear();
      const after = now.getMonth() >= 7;
      const start = after ? y : y - 1;
      return {
        startYear: start,
        endYear: start + 1,
        label: `${start}/${start + 1}`,
        startDate: `${start}-08-01`,
        endDate: `${start + 1}-05-30`,
      };
    },
    lastSeason() {
      const c = this.currentSeason;
      return {
        startYear: c.startYear - 1,
        endYear: c.endYear - 1,
        label: `${c.startYear - 1}/${c.endYear - 1}`,
        startDate: `${c.startYear - 1}-08-01`,
        endDate: `${c.endYear - 1}-05-30`,
      };
    },
  },
  async created() {
    await this.loadTeams();
  },
  methods: {
    avatar(pic) {
      return avatarUrl(pic || "default-image.png");
    },
    formatDob(d) {
      return dayjs(d).format("D MMM YYYY");
    },
    async loadTeams() {
      if (!this.userData.clubId) return;
      try {
        const res = await Teams.find({ action: "getTeamsDataById", clubId: this.userData.clubId });
        const list = Array.isArray(res?.data) ? res.data : [];
        this.teams = list
          .map((t) => ({ id: Number(t.id), teamName: t.teamName }))
          .sort((a, b) => a.teamName.localeCompare(b.teamName, undefined, { numeric: true }));
      } catch (e) {
        console.error("Testing: loadTeams", e);
        this.$toast?.error("Couldn't load squads");
      }
    },
    async loadPlayers() {
      this.selectedPlayerId = "";
      this.players = [];
      if (!this.selectedTeamId) return;
      this.loadingPlayers = true;
      try {
        const res = await PlayerPhysicalPerformance.find({
          action: "getPlayersForTesting",
          teamId: this.selectedTeamId,
        });
        this.players = (Array.isArray(res?.data) ? res.data : []).map((p) => ({
          ...p,
          id: Number(p.id),
        }));
      } catch (e) {
        console.error("Testing: loadPlayers", e);
        this.$toast?.error(e?.response?.data?.message || "Couldn't load players");
      } finally {
        this.loadingPlayers = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.testing-intro {
  color: #5b6b7a;
  margin-bottom: 1rem;
}

.testing-pickers {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  align-items: flex-end;
  margin-bottom: 1.5rem;

  .form__item {
    min-width: 16rem;
  }

  .player-nav {
    display: flex;
    gap: 0.5rem;
    min-width: auto;
  }
}

.player-strip {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  margin-bottom: 1rem;
  background: #f7f9fb;
  border: 1px solid #e0e5e9;
  border-radius: 10px;

  img {
    width: 56px;
    height: 56px;
    border-radius: 50%;
    object-fit: cover;
  }

  h2 {
    margin: 0;
    font-size: 1.25rem;
  }

  p {
    margin: 0;
    color: #5b6b7a;
    font-size: 0.9rem;
  }

  .bioband-btn {
    margin-left: auto;
  }
}

.empty-state,
.loading-state {
  text-align: center;
  padding: 3rem 1rem;
  color: #6c757d;
  border: 2px dashed #cbd5e1;
  border-radius: 10px;

  i {
    font-size: 2rem;
    display: block;
    margin-bottom: 0.5rem;
  }
}

.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
  padding: 1rem;
}

.modal-dialog {
  width: 100%;
  max-width: 1140px;
  max-height: 95vh;
  overflow: auto;
}
</style>
